import React, { useState, useEffect } from 'react';
import { Spinner, Collapse } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { workTypesAPI } from '../../helpers/api';

interface MaterialDetail {
  waybillNumber: string;
  itemCode: string;
  itemName: string;
  amount: number;
  price: number;
  total: number;
  unitName: string | null;
  vatAmount: number;
}

interface WorkCost {
  siteWorkId: string;
  workTypeName: string;
  unit: string;
  completedQuantity: number;
  targetQuantity: number | null;
  totalCost: number;
  totalVat: number;
  unitCost: number;
  materials: MaterialDetail[];
}

interface SiteCost {
  siteId: string;
  siteName: string;
  totalCost: number;
  totalVat: number;
  grandTotal: number;
  works: WorkCost[];
}

interface ProjectCostData {
  projectId: string;
  totalCost: number;
  totalVat: number;
  grandTotal: number;
  sites: SiteCost[];
}

interface Project {
  id: string;
  name: string;
  code: string;
  city: { name: string };
}

interface ProjectCostModalProps {
  isOpen: boolean;
  toggle: () => void;
  project: Project | null;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2,
  }).format(value);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const ProjectCostModal: React.FC<ProjectCostModalProps> = ({
  isOpen,
  toggle,
  project,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [costData, setCostData] = useState<ProjectCostData | null>(null);
  const [expandedSites, setExpandedSites] = useState<Set<string>>(new Set());
  const [expandedWorks, setExpandedWorks] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen && project) {
      fetchCostData();
    }
  }, [isOpen, project]);

  const fetchCostData = async () => {
    if (!project) return;
    try {
      setLoading(true);
      const res = await workTypesAPI.getProjectCost(project.id);
      setCostData(res.data);
      // Tüm şantiyeleri varsayılan olarak aç
      if (res.data.sites) {
        setExpandedSites(new Set(res.data.sites.map((s: SiteCost) => s.siteId)));
      }
    } catch (error) {
      console.error('Maliyet hesaplanamadı:', error);
      toast.error('Maliyet verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleSite = (siteId: string) => {
    setExpandedSites(prev => {
      const next = new Set(prev);
      if (next.has(siteId)) next.delete(siteId);
      else next.add(siteId);
      return next;
    });
  };

  const toggleWork = (workKey: string) => {
    setExpandedWorks(prev => {
      const next = new Set(prev);
      if (next.has(workKey)) next.delete(workKey);
      else next.add(workKey);
      return next;
    });
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!project) return null;

  const headerActions = (
    <button
      type="button"
      className="side-modal-expand-btn"
      onClick={toggleExpand}
      title={isExpanded ? 'Küçült' : 'Genişlet'}
    >
      <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
    </button>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Proje Maliyet Analizi"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
      hideBackdrop
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header */}
        <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }}>
          <h4 className="mb-1" style={{ fontWeight: 600 }}>{project.name}</h4>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {project.city.name} &bull; {project.code}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : !costData ? (
          <div className="text-center py-4">
            <p className="text-muted mb-0">Maliyet verisi bulunamadı</p>
          </div>
        ) : costData.sites.length === 0 ? (
          <div className="text-center py-4">
            <i className="ri-money-dollar-circle-line" style={{ fontSize: '2rem', color: '#adb5bd' }}></i>
            <p className="text-muted mt-2 mb-0">
              Henüz maliyet verisi yok.<br />
              <small>İş türlerine malzeme eşleştirmesi yapıldığında burada görünür.</small>
            </p>
          </div>
        ) : (
          <>
            {/* Toplam Maliyet Kartları */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
              <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Genel Toplam
              </div>
              <div className="d-flex gap-2">
                <div className="flex-fill rounded-3 p-3 text-center" style={{ background: '#f0f4ff', border: '1px solid #d0daf0' }}>
                  <div className="text-muted small mb-1">Tutar (KDV Hariç)</div>
                  <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#405189' }}>
                    {formatCurrency(costData.totalCost)}
                  </div>
                </div>
                <div className="flex-fill rounded-3 p-3 text-center" style={{ background: '#fff4e5', border: '1px solid #f0dfc0' }}>
                  <div className="text-muted small mb-1">KDV</div>
                  <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#b76e00' }}>
                    {formatCurrency(costData.totalVat)}
                  </div>
                </div>
                <div className="flex-fill rounded-3 p-3 text-center" style={{ background: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                  <div className="text-muted small mb-1">Genel Toplam</div>
                  <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#2e7d32' }}>
                    {formatCurrency(costData.grandTotal)}
                  </div>
                </div>
              </div>
            </div>

            {/* Şantiye Kırılımları */}
            <div className="rounded-3 p-3 mb-3" style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }}>
              <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Şantiye Kırılımları ({costData.sites.length} şantiye)
              </div>

              {costData.sites.map((site) => (
                <div key={site.siteId} className="mb-3 border rounded-3 overflow-hidden">
                  {/* Şantiye Header */}
                  <div
                    className="d-flex align-items-center justify-content-between px-3 py-3"
                    style={{ background: '#f8f9fa', cursor: 'pointer' }}
                    onClick={() => toggleSite(site.siteId)}
                  >
                    <div className="d-flex align-items-center">
                      <i className={`ri-arrow-${expandedSites.has(site.siteId) ? 'down' : 'right'}-s-line me-2 text-muted`}></i>
                      <i className="ri-building-line me-2 text-primary"></i>
                      <span style={{ fontWeight: 600 }}>{site.siteName}</span>
                    </div>
                    <span style={{ fontWeight: 600, color: '#2e7d32' }}>
                      {formatCurrency(site.grandTotal)}
                    </span>
                  </div>

                  <Collapse isOpen={expandedSites.has(site.siteId)}>
                    {/* Şantiye Toplam */}
                    <div className="d-flex gap-2 px-3 py-2" style={{ background: '#fafbfc', borderTop: '1px solid #e9ecef' }}>
                      <div className="small">
                        <span className="text-muted">Tutar:</span>{' '}
                        <span style={{ fontWeight: 500 }}>{formatCurrency(site.totalCost)}</span>
                      </div>
                      <div className="small">
                        <span className="text-muted">KDV:</span>{' '}
                        <span style={{ fontWeight: 500 }}>{formatCurrency(site.totalVat)}</span>
                      </div>
                    </div>

                    {/* İş Türleri */}
                    {site.works.map((work) => {
                      const workKey = `${site.siteId}-${work.siteWorkId}`;
                      return (
                        <div key={work.siteWorkId} style={{ borderTop: '1px solid #e9ecef' }}>
                          {/* İş Türü Header */}
                          <div
                            className="d-flex align-items-center justify-content-between px-3 py-2"
                            style={{ cursor: 'pointer', paddingLeft: '2rem' }}
                            onClick={() => toggleWork(workKey)}
                          >
                            <div className="d-flex align-items-center" style={{ paddingLeft: '1rem' }}>
                              <i className={`ri-arrow-${expandedWorks.has(workKey) ? 'down' : 'right'}-s-line me-2 text-muted`} style={{ fontSize: '0.85rem' }}></i>
                              <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{work.workTypeName}</span>
                              <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>({work.unit})</span>
                            </div>
                            <div className="d-flex align-items-center gap-3">
                              {work.unitCost > 0 && (
                                <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                                  Birim: {formatCurrency(work.unitCost)}
                                </span>
                              )}
                              <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                {formatCurrency(work.totalCost + work.totalVat)}
                              </span>
                            </div>
                          </div>

                          {/* İş bilgileri */}
                          <Collapse isOpen={expandedWorks.has(workKey)}>
                            <div style={{ paddingLeft: '3.5rem' }} className="pb-2 pe-3">
                              {/* Miktar bilgisi */}
                              {(work.completedQuantity > 0 || work.targetQuantity) && (
                                <div className="d-flex gap-3 mb-2" style={{ fontSize: '0.8rem' }}>
                                  <span className="text-muted">
                                    Yapılan: <strong>{formatNumber(work.completedQuantity)}</strong> {work.unit}
                                  </span>
                                  {work.targetQuantity && (
                                    <span className="text-muted">
                                      Hedef: <strong>{formatNumber(work.targetQuantity)}</strong> {work.unit}
                                    </span>
                                  )}
                                </div>
                              )}

                              {/* Malzeme Detayları */}
                              <div className="table-responsive">
                                <table className="table table-sm table-borderless mb-0" style={{ fontSize: '0.8rem' }}>
                                  <thead>
                                    <tr className="text-muted">
                                      <th style={{ fontWeight: 500 }}>Malzeme</th>
                                      <th style={{ fontWeight: 500 }}>İrsaliye</th>
                                      <th className="text-end" style={{ fontWeight: 500 }}>Miktar</th>
                                      <th className="text-end" style={{ fontWeight: 500 }}>Birim Fiyat</th>
                                      <th className="text-end" style={{ fontWeight: 500 }}>Tutar</th>
                                      <th className="text-end" style={{ fontWeight: 500 }}>KDV</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {work.materials.map((m, i) => (
                                      <tr key={i}>
                                        <td>
                                          <div style={{ fontWeight: 500 }}>{m.itemName}</div>
                                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>{m.itemCode}</div>
                                        </td>
                                        <td className="text-muted">{m.waybillNumber}</td>
                                        <td className="text-end">{formatNumber(m.amount)} {m.unitName || ''}</td>
                                        <td className="text-end">{formatCurrency(m.price)}</td>
                                        <td className="text-end" style={{ fontWeight: 500 }}>{formatCurrency(m.total)}</td>
                                        <td className="text-end">{formatCurrency(m.vatAmount)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr style={{ borderTop: '1px solid #dee2e6' }}>
                                      <td colSpan={4} className="text-end text-muted" style={{ fontWeight: 500 }}>Toplam:</td>
                                      <td className="text-end" style={{ fontWeight: 600 }}>{formatCurrency(work.totalCost)}</td>
                                      <td className="text-end" style={{ fontWeight: 600 }}>{formatCurrency(work.totalVat)}</td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                  </Collapse>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </SideModal>
  );
};

export default ProjectCostModal;
